// Personal settings (UX spec 6.15): appearance (theme and density), this browser's phone and audio
// devices, notifications and keyboard shortcuts.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useWorkspace } from '../../state/workspace.tsx';
import { viewAsPhoneDockCopy } from '../../state/viewAs.ts';
import { useDensity } from '../../state/density.ts';
import { getThemePreference, setThemePreference, subscribeTheme } from '../../lib/theme.ts';
import { detectPlatform, findShortcut, setSingleKeyShortcuts, shortcutText, singleKeyShortcutsOn, subscribeSingleKey } from '../../lib/shortcuts.ts';
import { formatAbsolute } from '../../lib/format.ts';
import { APP_NAME, APP_OWNER } from '../../config/brand.ts';
import { CLIENT_VERSION } from '../../config/phone.ts';
import { setShowCallerInNotifications, showCallerInNotifications } from '../../lib/notifications.ts';
import { useDesktop } from '../../desktop/bridge.ts';
import type { DeviceView } from '../../call/deviceView.ts';
import type { BridgeSnapshot } from '../../call/bridge.ts';
import { PhoneSetup } from '../phone/PhoneDock.tsx';
import { MicrophoneWarning, IPhoneInstallCard } from '../phone/MicrophoneHelp.tsx';
import { Diagnostics } from '../../ui/Diagnostics.tsx';
import { TwoStepSection } from './TwoStepSection.tsx';
import { SpoolPanel } from '../spool/SpoolPanel.tsx';
import { Link } from '../../lib/router.tsx';
import { DropLibrary } from '../voicemail/DropLibrary.tsx';
import { dropAccess } from '../voicemail/drops.ts';
import { DesktopInstallCard } from './DesktopInstallCard.tsx';
import { MailboxSettingsTile } from '../phone/PostCallFollowup.tsx';

export function SettingsPage() {
  const ws = useWorkspace();
  // Theme and density also change from the command palette; both are read from their stores.
  const theme = useSyncExternalStore(subscribeTheme, getThemePreference, () => 'system' as const);
  const [density, setDensity] = useDensity();
  const singleKey = useSyncExternalStore(subscribeSingleKey, singleKeyShortcutsOn, () => true);
  const platform = detectPlatform();
  const [notify, setNotify] = useState<NotificationPermission | 'unsupported'>(() => (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission));
  const [deviceMessage, setDeviceMessage] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const desktop = useDesktop();
  const [showCaller, setShowCaller] = useState(showCallerInNotifications);
  // On desktop the main process applies the notification detail setting; read and write it there.
  useEffect(() => {
    if (!desktop) return;
    let live = true;
    desktop.settings.get().then(v => { if (live) setShowCaller(v.notification_detail === 'show_caller'); }, () => undefined);
    return () => { live = false; };
  }, [desktop]);
  const changeShowCaller = (show: boolean) => {
    setShowCaller(show);
    if (desktop) void desktop.settings.set({ notification_detail: show ? 'show_caller' : 'hidden' }).then(v => setShowCaller(v.notification_detail === 'show_caller'), () => undefined);
    else setShowCallerInNotifications(show);
  };
  const cfg = ws.config.value;
  const media = ws.bridge.media;
  const engineRunning = ws.bridge.engine === 'call-client' && ws.bridge.phone.state !== 'unavailable';
  const phoneOn = engineRunning && ws.bridge.phone.state !== 'stopped' && ws.bridge.phone.state !== 'revoked';
  const liveCall = ws.liveCalls.length > 0;
  const viewAs = ws.session.view_as ?? null;
  const readOnly = Boolean(viewAs);

  const choose = async (kind: 'input' | 'output', id: string) => {
    setDeviceMessage(null);
    const error = kind === 'input' ? await ws.phone.selectInput(id || null) : await ws.phone.selectOutput(id || null);
    if (error) setDeviceMessage(error);
  };

  return (
    <div className="page settings">
      <h1 className="page-title">Settings</h1>
      {viewAs ? (
        <div className="notice notice-info view-as-settings-notice" role="note">
          <p>Viewing as {viewAs.display_name}. Settings are read-only in this mode.</p>
        </div>
      ) : null}
      <div className="settings-grid">
        <IPhoneInstallCard />
        <section className="settings-tile" aria-labelledby="set-phone">
          <h2 id="set-phone" className="section-title">Phone on this browser</h2>
          <dl className="facts">
            <div><dt>Status</dt><dd>{ws.readiness.label}</dd></div>
            <div><dt>Microphone</dt><dd>{micText(ws.mic)}</dd></div>
            <div><dt>This browser</dt><dd>{ws.device ? 'Set up as your phone' : 'Not set up'}</dd></div>
            <div><dt>Phone connection</dt><dd>{connectionText(ws.device, ws.bridge.bootstrap)}</dd></div>
          </dl>
          {viewAs ? <p className="inline-reason">{viewAsPhoneDockCopy(viewAs)}</p> : <PhoneSetup reasonId="settings-phone-why" />}
          <MicrophoneWarning />
          <div className="form-actions">
            {phoneOn && ws.readiness.state !== 'ready' ? <button type="button" className="btn" onClick={ws.phone.retry}>Reconnect now</button> : null}
            {phoneOn ? (
              <button type="button" className="btn btn-quiet" onClick={() => void ws.phone.turnOff()} disabled={liveCall} title={liveCall ? 'Finish your call first.' : undefined}>Turn phone off</button>
            ) : null}
            {ws.device && !confirmRemove ? (
              <button type="button" className="btn btn-quiet" onClick={() => setConfirmRemove(true)} disabled={liveCall} title={liveCall ? 'Finish your call first.' : undefined}>Remove this browser</button>
            ) : null}
          </div>
          {confirmRemove ? (
            <div className="notice notice-warn" role="alertdialog" aria-labelledby="remove-q">
              <p id="remove-q">Remove this browser as your phone? Calls won’t ring here until you set it up again.</p>
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setConfirmRemove(false)}>Keep it</button>
                <button type="button" className="btn btn-end" onClick={() => { setConfirmRemove(false); void ws.forgetDevice(); }}>Remove</button>
              </div>
            </div>
          ) : null}
        </section>

        <section className="settings-tile" aria-labelledby="set-audio">
          <h2 id="set-audio" className="section-title">Audio devices</h2>
          {!engineRunning ? (
            <p className="field-hint">Audio devices appear here once this browser is connected as your phone.</p>
          ) : (
            <>
              {ws.mic !== 'granted' ? <p className="field-hint">Allow the microphone to see device names.</p> : null}
              <div className="field">
                <label htmlFor="set-input">Microphone</label>
                <select id="set-input" value={media.selectedInput ?? ''} onChange={e => void choose('input', e.target.value)}>
                  <option value="">System default</option>
                  {media.inputs.filter(d => d.id !== 'default').map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="set-output">Speaker</label>
                {media.outputSelection === 'supported' ? (
                  <select id="set-output" value={media.selectedOutput ?? ''} onChange={e => void choose('output', e.target.value)}>
                    <option value="">System default</option>
                    {media.outputs.filter(d => d.id !== 'default').map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                ) : (
                  <p className="field-hint" id="set-output">Audio plays through your system output in this browser.</p>
                )}
              </div>
              {deviceMessage ? <p className="field-error" role="alert">{deviceMessage}</p> : null}
            </>
          )}
        </section>

        {/* Round 13: the voicemail drops for this person's direct line (the number's own drops come first on a call). */}
        <div className="settings-tile settings-wide" id="my-voicemail-drops">
          <DropLibrary owner={{ scope: 'user_line', userId: ws.userId }} title="My voicemail drops" canManage={!readOnly && dropAccess(ws.session).manageOwn} />
        </div>

        <section className="settings-tile" aria-labelledby="set-appearance">
          <h2 id="set-appearance" className="section-title">Appearance</h2>
          <fieldset className="radio-row">
            <legend>Theme</legend>
            {(['system', 'day', 'night'] as const).map(t => (
              <label key={t} className="radio">
                <input type="radio" name="theme" value={t} checked={theme === t} disabled={readOnly} onChange={() => setThemePreference(t)} />
                {t === 'system' ? 'Match system' : t === 'day' ? 'Day' : 'Night'}
              </label>
            ))}
          </fieldset>
          <fieldset className="radio-row" aria-describedby="set-density-hint">
            <legend>Density</legend>
            {(['comfortable', 'compact'] as const).map(d => (
              <label key={d} className="radio">
                <input type="radio" name="density" value={d} checked={density === d} disabled={readOnly} onChange={() => setDensity(d)} />
                {d === 'comfortable' ? 'Comfortable' : 'Compact'}
              </label>
            ))}
          </fieldset>
          <p id="set-density-hint" className="field-hint">Compact fits more calls and contacts on screen by tightening rows, lists and the phone panel. Text size and tap targets stay the same. Saved for you in this browser.</p>
        </section>

        <section className="settings-tile" aria-labelledby="set-keys">
          <h2 id="set-keys" className="section-title">Keyboard shortcuts</h2>
          <p className="field-hint">Press {shortcutText(findShortcut('palette')?.keys ?? [], platform)} anywhere to search pages, teammates and calls, or to run an action.</p>
          <label className="check">
            <input type="checkbox" checked={singleKey} disabled={readOnly} onChange={e => setSingleKeyShortcuts(e.target.checked)} />
            Press ? to show the list of shortcuts
          </label>
          <p className="field-hint">Turn this off if you use speech input or a screen reader that sends single keys. The list is still in the account menu.</p>
        </section>

        <MailboxSettingsTile readOnly={readOnly} />
        <DesktopInstallCard />
        <section className="settings-tile" aria-labelledby="set-notify">
          <h2 id="set-notify" className="section-title">Notifications</h2>
          <p className="field-hint">When the phone’s app window or tab is minimized or unfocused, it can show “Incoming call”. Click the notification to open the ringing phone, then use Answer. Only the phone owner notifies. Sidetone must stay open and connected.</p>
          {notify === 'unsupported' ? <p className="inline-reason">This browser doesn’t support notifications.</p> : notify === 'granted' ? <p className="meta">Desktop notifications are on.</p> : notify === 'denied' ? <p className="inline-reason">Notifications are blocked in your browser’s site settings.</p> : (
            <button type="button" className="btn" disabled={readOnly} onClick={() => void Notification.requestPermission().then(setNotify)}>Turn on desktop notifications</button>
          )}
          <label className="check">
            <input type="checkbox" checked={showCaller} disabled={readOnly} onChange={e => changeShowCaller(e.target.checked)} />
            Show the caller’s name or number in notifications
          </label>
          <p className="field-hint">In-app notification categories and quiet hours are configured separately. SMS phone alerts and email verification codes are disabled; use an authenticator app for two-step verification.</p>
          <Link to="/settings/notifications" className="btn btn-small">Notification preferences</Link>
        </section>

        <TwoStepSection />

        <SpoolPanel />

        <section className="settings-tile settings-wide" aria-labelledby="set-about">
          <h2 id="set-about" className="section-title">About</h2>
          <dl className="facts">
            <div><dt>Product</dt><dd>{APP_NAME} by {APP_OWNER}</dd></div>
            <div><dt>Version</dt><dd className="mono">{CLIENT_VERSION}</dd></div>
            <div><dt>Environment</dt><dd>{cfg ? (cfg.environment === 'production' ? 'Production' : cfg.environment === 'staging' ? 'Staging' : 'Development') : 'Unknown'}{cfg?.data_mode === 'fixture' ? ', fixture data (not real calls)' : ''}</dd></div>
            <div><dt>Workspace time zone</dt><dd>{cfg?.tenant_timezone ?? 'Unknown'}</dd></div>
          </dl>
          <p className="field-hint">For help with your account, numbers or phone setup, contact your workspace administrator.</p>
          <Diagnostics diagnostic={{ code: cfg?.build_id ? `build ${cfg.build_id}` : null, detail: `Phone engine: ${ws.bridge.engine === 'call-client' ? 'browser call engine' : 'none'}${ws.bridge.phone.issues.length ? `. Issues: ${ws.bridge.phone.issues.join(', ')}` : ''}.` }} />
        </section>
      </div>
    </div>
  );
}

/** Whether the phone system has issued this browser credentials; never shows the credentials. */
function connectionText(device: DeviceView | null, engine: BridgeSnapshot['bootstrap']): string {
  if (!device) return 'Not applicable';
  const status = engine?.status ?? device.bootstrap?.status ?? null;
  const expires = engine?.expiresAt ?? (device.bootstrap?.status === 'issued' ? device.bootstrap.expires_at : null);
  if (status === 'issued' || status === 'ready') return expires ? `Issued, renews before ${formatAbsolute(expires)}` : 'Issued';
  if (status === 'expired') return 'Expired, renewing';
  if (status === null) return 'Requested when the phone connects';
  return 'Not ready yet. An administrator needs to finish setup.';
}

function micText(mic: string): string {
  switch (mic) {
    case 'granted':
      return 'Allowed';
    case 'denied':
      return 'Blocked in browser settings';
    case 'prompt':
      return 'Not asked yet';
    case 'unsupported':
      return 'Not available in this browser';
    default:
      return 'Unknown';
  }
}
